import React from 'react';
import { makeStyles, withStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { Breadcrumbs, Chip } from '@material-ui/core';
import { GraySmall, HoverNonBorderBackground } from '../../../theme/colors';
import BaseTypography from '../../Text/BaseTypography';
import { ForwardIcon } from '../../Icons';
import { RegularButtonHoverShadow } from '../../../theme/shadows';
import useNavigate from '../../../hooks/useNavigate';

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbsRoot: {
    overflow: 'hidden',
  },
  breadcrumbsListContainer: {
    flexWrap: 'nowrap',
  },
  breadcrumbListItem: {
    '&:last-child': {
      overflow: 'hidden',
    },
  },
  separator: {
    margin: theme.spacing(0, 1.5),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, 0.5),
    },
    '& svg': {
      height: '7px',
      width: '7px',
    },
  },
  node: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    '&::first-letter': {
      textTransform: props => props.textTransform ?? 'uppercase',
    },
  },
}));
const StyledChip = withStyles((theme: Theme) => ({
  root: {
    backgroundColor: '#fff',
    cursor: 'pointer',
    height: theme.spacing(3),
    color: GraySmall,
    borderRadius: theme.shape.borderRadius,
    fontWeight: theme.typography.fontWeightRegular,
    '& > .MuiChip-label': {
      padding: theme.spacing(0, 0.5),
    },
    '&:hover, &:focus': {
      backgroundColor: HoverNonBorderBackground,
    },
    '&:active': {
      boxShadow: RegularButtonHoverShadow,
    },
  },
}))(Chip) as typeof Chip;

export interface BreadCrumbItem {
  label: string;
  path?: string;
  textTransform?:
    | 'capitalize'
    | 'full-size-kana'
    | 'full-width'
    | 'lowercase'
    | 'none'
    | 'uppercase';
}
const BreadCrumbItem: React.FC<BreadCrumbItem> = ({
  label,
  path,
  textTransform,
}) => {
  const classes = useStyles({ textTransform });
  const { navigate } = useNavigate();

  const handleRedirection = React.useCallback(() => {
    if (path) {
      navigate(path);
    }
  }, [navigate, path]);

  // if path is not provided, then wrap the label in a chip, otherwise, wrap the label in normal text
  return path ? (
    <StyledChip
      key={path}
      label={
        <BaseTypography fontType="13Medium" className={classes.node}>
          {label}
        </BaseTypography>
      }
      onClick={handleRedirection}
    />
  ) : (
    <BaseTypography fontType="13Medium" className={classes.node}>
      {label}
    </BaseTypography>
  );
};
export interface BreadCrumbBaseProps {
  items: BreadCrumbItem[];
}
export const BreadCrumbBase: React.FC<BreadCrumbBaseProps> = ({ items }) => {
  const classes = useStyles();

  return (
    <Breadcrumbs
      classes={{
        separator: classes.separator,
        root: classes.node,
        ol: classes.breadcrumbsListContainer,
        li: classes.breadcrumbListItem,
      }}
      className={classes.breadcrumbsRoot}
      separator={<ForwardIcon />}
      aria-label="breadcrumb"
      color="initial"
    >
      {items.map(({ label, path, textTransform }) => (
        <BreadCrumbItem
          key={label}
          label={label}
          path={path}
          textTransform={textTransform}
        />
      ))}
    </Breadcrumbs>
  );
};
